<template lang="pug">
div._root
    router-view

Footer

Teleport(to="body")
    TransitionGroup.bq-toast-container(name="bq-toast" tag="div")
        div.bq-toast(v-for="t in toasts" :key="t.id" :class="`bq-toast--${t.type}`")
            span.bq-toast__msg {{ t.message }}#[a.bq-toast__link(v-if="t.link" :href="t.link.href" target="_blank" rel="noopener") {{ t.link.label }}]
            button.bq-toast__close(type="button" @click="dismissToast(t.id)" aria-label="Dismiss") ×
</template>

<script setup>
import Footer from "@/components/footer.vue";
import { toasts, dismissToast } from "@/components/toast";
import { initKeyboardInset } from "@/code/keyboard-inset";

initKeyboardInset();
</script>
<style lang="less">
@import "@/assets/less/bq-tokens.less";

/* Sticky footer. The shell is a column at least as tall as the viewport: the
   content area takes whatever height is left over and the footer keeps its
   own, so on a page shorter than the screen the footer sits on the bottom
   edge, from the first paint and at every width.
   (It used to be a min-height of `calc(99vh - var(--footer-height))` on the
   content area, with the footer measuring itself on mount and writing that
   variable to the body. Until that ran the fallback made it `calc(99vh - 0)`,
   which is invalid inside calc() because the zero carries no unit, so the
   declaration was dropped and the footer rode up under short content. The
   measured value also went stale whenever the footer re-wrapped.) */
#app {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
}

/* The visible viewport on mobile, so a short page does not scroll and the
   footer lands on the bottom edge instead of just below it. This is a
   separate rule rather than a second declaration because the CSS minifier
   collapses two declarations of one property and would drop the fallback. */
@supports (min-height: 100dvh) {
    #app {
        min-height: 100dvh;
    }
}

/* On-screen keyboard up (code/keyboard-inset.ts). Where the keyboard covers
   the page instead of resizing it, the last fields of a form sit behind the
   keyboard with no page left to scroll. The covered height is added as
   scroll room, so any field can be brought above the keyboard. */
html.kb-open #app {
    padding-bottom: var(--kb, 0px);
}

._root {
    flex: 1 0 auto;
}

#footer {
    flex-shrink: 0;
}

/* Bottom centre. The stack is centred on the viewport rather than pinned to a
   corner, and align-items keeps each toast at its own width so they centre on
   each other instead of all stretching to the container. The enter/leave
   transforms live on the children, so the centring translate here is safe. */
.bq-toast-container {
    position: fixed;
    bottom: 1.5rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    max-width: min(28rem, calc(100vw - 2rem));
    pointer-events: none;
}

/* Above the on-screen keyboard, not behind it. */
html.kb-open .bq-toast-container {
    bottom: calc(1.5rem + var(--kb, 0px));
}

.bq-toast {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    /* The container used to force this width by stretching its items. Centring
       them makes the width content-based instead, so the cap has to be restated
       here or an unbreakable message could push the toast past the container. */
    max-width: 100%;
    box-sizing: border-box;
    padding: 0.75rem 1rem;
    /* Square, with the hard offset shadow: the same extruded language as the
       cards, buttons, modals and dropdown panels. A soft blurred radius read as
       a different product. */
    border-radius: 0;
    border: 1px solid;
    box-shadow: 2px 2px var(--bq-shadow);
    pointer-events: all;
    line-height: 1.4;
    font-size: 0.875rem;

    &--error   { background: @danger-bg;  border-color: @danger;   color: @danger;   }
    &--warning { background: @warning-bg; border-color: @warning-border; color: @warning; }
    &--info    { background: @info-bg;    border-color: @info-border;    color: @info;    }
    &--success { background: @success-bg; border-color: @success-border; color: @success; }
}

.bq-toast__msg {
    flex: 1;
    /* Lets the text shrink below its content width so the cap above can hold. */
    min-width: 0;
    color: inherit;
    word-break: break-word;
}

/* The docs link a toast may carry: same colour as the sentence, underlined. */
.bq-toast__link {
    margin-left: 0.35rem;
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
    white-space: nowrap;
}

.bq-toast__close {
    flex-shrink: 0;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    font-size: 1.1rem;
    line-height: 1;
    color: inherit;
    opacity: 0.6;
    &:hover { opacity: 1; }
}

/* Phones and small tablets: pin the stack to both edges instead of centring a
   content-width pill, so a sentence gets the whole screen to wrap in. The 28rem
   cap is a desktop reading-width limit and only makes the box narrower here. */
@media (max-width: 600px) {
    .bq-toast-container {
        left: 1rem;
        right: 1rem;
        transform: none;
        max-width: none;
        align-items: stretch;
    }
}

.bq-toast-enter-active,
.bq-toast-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}
.bq-toast-enter-from { opacity: 0; transform: translateY(0.5rem); }
.bq-toast-leave-to   { opacity: 0; transform: translateY(0.5rem); }

</style>
